import { Button } from "@/components/ui/baseUi/button"
import { Calendar } from "@/components/ui/calendar"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { getFirstErrorMessage } from "@/components/ui/forms/helpers/formErrorHelpers"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { ApiFinanceType } from "@modules/finances/types/financesType"
import { downloadFile } from "@modules/utils/downloadFile"
import { LoggerType } from "@modules/utils/loggerUtils"
import * as Sentry from "@sentry/react"
import dayjs from "dayjs"
import { ChevronDown, LoaderCircle } from "lucide-react"
import { useEffect, useMemo } from "react"
import { z } from "zod"

export type ExportModalRef = {
  open: () => void
}

type Props = {
  onHide: () => void
  visible: boolean
  financeApiType: ApiFinanceType
  className?: string
} & React.ComponentProps<"div">

export function ExportModal({ onHide, visible, financeApiType, className, ...props }: Props) {
  const [period] = usePeriodParam()
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const financeApi = financeApiType === ApiFinanceType.BOX ? boxFinanceApi : userFinanceApi

  const maxDate = useMemo(() => new Date(dayjs(period).endOf("month").toDate()), [period])

  const exportFormSchema = z
    .object({
      dateStart: z.date(),
      dateEnd: z.date(),
    })
    .refine((values) => !dayjs(values.dateStart).isAfter(dayjs(values.dateEnd)), {
      message: word("finances.page.export.modal.validation.startDateAfterEndDate"),
      path: ["dateStart"],
    })

  const form = useAppForm({
    defaultValues: {
      dateStart: dayjs(period).startOf("month").toDate(),
      dateEnd: dayjs(period).endOf("month").toDate(),
    } satisfies z.infer<typeof exportFormSchema>,
    validators: {
      onChange: exportFormSchema,
      onSubmit: exportFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await financeApi.exportTransactions(
          dayjs(value.dateStart).toISOString(),
          dayjs(value.dateEnd).toISOString(),
        )
        downloadFile("transaction.csv", result, "text/csv")
        toast.success(word("finances.page.export.success"), { classNames: successToastClassName() })
        onHide()
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        Sentry.captureException(`${LoggerType.Financial} Export data ${message}`)
        toast.error(message, { classNames: errorToastClassName() })
      }
    },
  })

  // The modal stays mounted across openings, so realign the date range on the
  // current period each time it opens (or when the period changes).
  useEffect(() => {
    if (!visible) return
    form.reset({
      dateStart: dayjs(period).startOf("month").toDate(),
      dateEnd: dayjs(period).endOf("month").toDate(),
    })
  }, [visible, period, form])

  return (
    <Dialog open={visible} onOpenChange={onHide}>
      <DialogContent className={cn("flex w-130 flex-col gap-4", className)} {...props}>
        <DialogHeader className="flex flex-col gap-4">
          <DialogTitle>{word("finances.page.export.modal.header")}</DialogTitle>
          <DialogDescription className="flex flex-col items-start gap-1 text-left">
            <Text fontStyle="TM">{word("finances.page.export.modal.title")}</Text>
            <Text fontStyle="BM">{word("finances.page.export.modal.description")}</Text>
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-row gap-4">
          <form.AppField name="dateStart">
            {(field) => (
              <field.Field className="grow">
                <field.Label required>{word("global.startDate")}</field.Label>
                <Popover>
                  <PopoverTrigger
                    render={
                      <Button variant="outline">
                        {field.state.value.toLocaleDateString()}
                        <ChevronDown />
                      </Button>
                    }
                  />
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.state.value}
                      captionLayout="label"
                      onSelect={(date) => {
                        field.handleChange(date ?? field.state.value)
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </field.Field>
            )}
          </form.AppField>
          <form.AppField name="dateEnd">
            {(field) => (
              <field.Field className="grow">
                <field.Label required>{word("global.endDate")}</field.Label>
                <Popover>
                  <PopoverTrigger
                    render={
                      <Button variant="outline">
                        {field.state.value.toLocaleDateString()}
                        <ChevronDown />
                      </Button>
                    }
                  />
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.state.value}
                      captionLayout="label"
                      onSelect={(date) => {
                        field.handleChange(date ?? field.state.value)
                      }}
                      disabled={{ after: maxDate }}
                    />
                  </PopoverContent>
                </Popover>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <form.Subscribe selector={(state) => state.fieldMeta.dateStart?.errors}>
          {(errors) => {
            const message = getFirstErrorMessage(errors)
            return message ? <p className="label-small text-textError">{message}</p> : null
          }}
        </form.Subscribe>

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onHide}>
            {word("global.cancel")}
          </Button>

          <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit] as const}>
            {([isSubmitting, canSubmit]) => (
              <Button
                variant="primary"
                onClick={() => void form.handleSubmit()}
                disabled={!canSubmit || isSubmitting}
                className="ml-2"
                isLoading={isSubmitting}
              >
                {isSubmitting && <LoaderCircle className="animate-spin" />}
                {word("global.export")}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </DialogContent>
    </Dialog>
  )
}
